import { useQuery } from '@tanstack/react-query';

import { discoveryKeys } from '@/shared/lib/query/queryKeys';

import type { OpenMetadataResponse } from '@/entities/search';

import { fetchEntityTypeChildren, fetchInitialTree, fetchTreeChildren } from '@/features/discovery/api/treeApi';

export const useInitialTree = () => {
  return useQuery<OpenMetadataResponse>({
    queryKey: discoveryKeys.initialTree(),
    queryFn: fetchInitialTree,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useEntityTypeChildren = (params: {
  entityType: string;
  index: string;
  useAggregation: boolean;
  entityTypes?: string[];
  serviceType?: string;
  serviceName?: string;
  databaseName?: string;
  schemaName?: string;
  enabled?: boolean;
}) => {
  const {
    entityType,
    index,
    useAggregation,
    entityTypes,
    serviceType,
    serviceName,
    databaseName,
    schemaName,
    enabled = true,
  } = params;

  return useQuery<OpenMetadataResponse>({
    queryKey: discoveryKeys.entityTypeChildren(
      entityType,
      index,
      useAggregation,
      entityTypes,
      serviceType,
      serviceName,
      databaseName,
      schemaName
    ),
    queryFn: () =>
      fetchEntityTypeChildren({
        entityType,
        index,
        useAggregation,
        entityTypes,
        serviceType,
        serviceName,
        databaseName,
        schemaName,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

type UseTreeChildrenParams = {
  nodeType: 'entityType' | 'serviceType' | 'service' | 'database' | 'schema';
  index?: string;
  entityType?: string;
  serviceType?: string;
  serviceName?: string;
  databaseName?: string;
  schemaName?: string;
  enabled?: boolean;
};

export const useTreeChildren = ({
  nodeType,
  index,
  entityType,
  serviceType,
  serviceName,
  databaseName,
  schemaName,
  enabled = true,
}: UseTreeChildrenParams) => {
  return useQuery<OpenMetadataResponse>({
    queryKey: discoveryKeys.treeChildren(
      nodeType,
      index,
      entityType,
      serviceType,
      serviceName,
      databaseName,
      schemaName
    ),
    queryFn: () =>
      fetchTreeChildren(nodeType, {
        index,
        entityType,
        serviceType,
        serviceName,
        databaseName,
        schemaName,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
